/**
 * The finance ledger — read-only, and it says so at the top of the screen.
 *
 * **A wrong entry is fixed with a compensating entry, never by editing or deleting a line.** That
 * is not a UI preference; it is what makes the ledger evidence. There is no "post correction"
 * button here because there is no endpoint behind one: a correction is a typed event emitted by
 * the service that owns it, with a reason and a second pair of eyes. A screen that offered a
 * free-form journal would put the chart of accounts in the hands of whoever could open it, and the
 * first mistake would be the plausible one — right amounts, wrong account, balancing perfectly and
 * wrong forever.
 *
 * **Every figure here is the server's.** The trial balance, its delta and each transaction's total
 * arrive computed. The console does not add up money: two places that compute a total are two
 * totals, and this is the copy nobody can audit.
 *
 * **The trial balance says when it was computed.** A number with no timestamp is one somebody
 * quotes in a meeting three days after it stopped being true — and "never computed" is rendered as
 * itself rather than as an empty table, which would read as "no money moved".
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { FilterBar, Money, Section, StatTile } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useChartOfAccounts,
  useJournal,
  useRebuildTrialBalance,
  useTrialBalance,
  type JournalRow,
} from '../lib/api/hooks/ledger';
import { AccountEditor, AddAccount, JournalExport } from './FinanceTools';
import { useUrlTab } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 25;

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'payment_captured', label: 'Captures' },
  { key: 'booking_recognised', label: 'Recognitions' },
  { key: 'reversal', label: 'Corrections' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same queue. */
const TAB_KEYS = TABS.map((t) => t.key);

/** The current month as `YYYY-MM`, which is the grain finance works in. */
export function currentMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * What a journal row is, in one word.
 *
 * A correction and the thing it corrects are both real entries and neither is wrong — saying so
 * is the difference between a reader who understands the chain and one who thinks they have
 * found a duplicate.
 */
export function rowKind(row: JournalRow): { label: string; tone: 'info' | 'warn' | 'neutral' } {
  if (row.reverses_ledger_transaction_id) return { label: 'correction', tone: 'warn' };
  if (row.posted_by === 'admin') return { label: 'posted by a person', tone: 'info' };
  return { label: 'automatic', tone: 'neutral' };
}

export function Ledger() {
  const { go } = useConsole();
  const [tab, setTab] = useUrlTab('ledger', TAB_KEYS, 'all');
  const [code, setCode] = useState('');
  const [page, setPage] = useState(1);
  const [month] = useState(currentMonth);
  const [editing, setEditing] = useState<string | null>(null);

  const journal = useJournal({
    txn_type: tab === 'all' ? undefined : tab,
    booking_code: code || undefined,
    page,
    page_size: PAGE_SIZE,
  });
  const trial = useTrialBalance(month);
  const accounts = useChartOfAccounts();
  const rebuild = useRebuildTrialBalance();
  /* Looked up on every render, so a save shows the server's copy rather than the one clicked. */
  const editingAccount = accounts.data?.find((account) => account.code === editing);

  const rows = journal.data?.items ?? [];
  const total = journal.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /** Any narrowing returns to page one — page 4 of a different filter is somebody else's page 4. */
  const narrow = (apply: () => void) => {
    apply();
    setPage(1);
  };

  return (
    <>
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        The ledger is append-only. Nothing on this screen edits or deletes a line — a mistake is
        corrected by a new compensating entry that references the one it reverses, posted by the
        service that owns the event rather than typed in here.
      </p>

      <Section
        title={`Trial balance · ${month}`}
        note={
          trial.data?.computed_at
            ? `computed ${formatDateTime(trial.data.computed_at)}`
            : 'never computed for this month'
        }
        actions={
          <Button
            variant="secondary"
            size="sm"
            disabled={rebuild.isPending}
            onClick={() => rebuild.mutate(month)}
          >
            {rebuild.isPending ? 'Rebuilding…' : 'Rebuild now'}
          </Button>
        }
      >
        <p style={{ margin: '0 0 var(--sp-md)', fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          Rebuilding is safe: the snapshot is derived from the journal and can be recomputed at any
          time. It changes no fact, only how fresh this table is.
        </p>

        {trial.isPending && <LoadingState message="Reading the trial balance…" />}
        {trial.isError && (
          <ErrorState title="Could not read the trial balance" cause={errorMessage(trial.error)} />
        )}
        {trial.isSuccess && trial.data.rows.length === 0 && (
          <EmptyState
            icon="scale"
            title={trial.data.computed_at ? 'No postings this month' : 'Not computed yet'}
            body={
              trial.data.computed_at
                ? 'Nothing was booked in this month.'
                : 'Rebuild to compute the month from the journal.'
            }
            inline
          />
        )}
        {trial.isSuccess && trial.data.rows.length > 0 && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: 'var(--sp-md)',
                marginBottom: 'var(--sp-md)',
              }}
            >
              <StatTile
                label="Debits"
                value={<Money paise={trial.data.total_debit_paise} />}
                note="this month"
              />
              <StatTile
                label="Credits"
                value={<Money paise={trial.data.total_credit_paise} />}
                note="this month"
              />
              <StatTile
                label="Out by"
                value={<Money paise={trial.data.delta_paise} />}
                note={
                  trial.data.delta_paise === 0
                    ? 'balanced to the paisa'
                    : 'the books do not tie — this is an incident'
                }
                tone={trial.data.delta_paise === 0 ? 'default' : 'error'}
              />
            </div>
            <DataTable
              density="compact"
              columns={[
                { key: 'code', label: 'Code', mono: true },
                { key: 'name', label: 'Account' },
                { key: 'type', label: 'Type' },
                { key: 'debit', label: 'Debits', align: 'right' },
                { key: 'credit', label: 'Credits', align: 'right' },
                { key: 'closing', label: 'Closing', align: 'right' },
              ]}
              rows={trial.data.rows.map((row) => ({
                id: row.account_code,
                code: row.account_code,
                name: row.account_name,
                type: row.account_type,
                debit: <Money paise={row.debit_paise} />,
                credit: <Money paise={row.credit_paise} />,
                closing: <Money paise={row.closing_paise} bold />,
              }))}
            />
          </>
        )}
      </Section>

      <Section
        title="Chart of accounts"
        note="codes are never reused — a retired account is deactivated and keeps its history"
        pad={false}
      >
        {accounts.isPending && <LoadingState message="Reading the chart…" />}
        {accounts.isError && (
          <ErrorState title="Could not read the chart" cause={errorMessage(accounts.error)} />
        )}
        {accounts.isSuccess && (
          <DataTable
            density="compact"
            onRowClick={(row) => setEditing(String(row.id))}
            columns={[
              { key: 'code', label: 'Code', mono: true },
              { key: 'name', label: 'Account' },
              { key: 'type', label: 'Type' },
              { key: 'side', label: 'Increases on' },
              { key: 'state', label: 'State' },
            ]}
            rows={accounts.data.map((account) => ({
              id: account.code,
              code: account.code,
              name: account.name,
              type: account.account_type,
              side: account.normal_side,
              state: (
                <StatusChip
                  tone={account.is_active ? 'success' : 'neutral'}
                  label={
                    account.is_active
                      ? account.has_history
                        ? 'in use'
                        : 'open, nothing posted'
                      : 'retired'
                  }
                />
              ),
            }))}
          />
        )}
      </Section>

      {editingAccount && (
        <AccountEditor
          key={editingAccount.id}
          account={editingAccount}
          onClose={() => setEditing(null)}
        />
      )}
      <AddAccount />
      <JournalExport />

      <FilterBar
        q={code}
        onQ={(value) => narrow(() => setCode(value))}
        placeholder="Booking code — BMP… — press /"
        tabs={TABS}
        tab={tab}
        onTab={(next) => narrow(() => setTab(next))}
      />

      {journal.isPending && <LoadingState message="Reading the journal…" />}
      {journal.isError && (
        <ErrorState title="Could not read the journal" cause={errorMessage(journal.error)} />
      )}
      {journal.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="scale"
          title="Nothing posted yet"
          body="The first captured payment writes the first journal entry."
          inline
        />
      )}

      {rows.length > 0 && (
        <Section
          title="Journal"
          note={`${total} ${total === 1 ? 'transaction' : 'transactions'} · page ${page} of ${pages}`}
          pad={false}
        >
          <DataTable
            density="compact"
            onRowClick={(row) => go('ledgertxn', { txnId: String(row.id) })}
            columns={[
              { key: 'occurred', label: 'Occurred', mono: true },
              { key: 'type', label: 'Event' },
              { key: 'booking', label: 'Booking', mono: true },
              { key: 'legs', label: 'Legs', align: 'right' },
              { key: 'amount', label: 'Amount', align: 'right' },
              { key: 'kind', label: 'Posted' },
            ]}
            rows={rows.map((row) => {
              const kind = rowKind(row);
              return {
                id: row.id,
                occurred: formatDateTime(row.occurred_at),
                type: row.txn_type,
                booking: row.booking_public_code ?? '—',
                legs: row.legs.length,
                amount: <Money paise={row.total_paise} />,
                kind: <StatusChip tone={kind.tone} label={kind.label} />,
              };
            })}
          />
        </Section>
      )}

      {rows.length > 0 && pages > 1 && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </>
  );
}
